import { useState, useCallback } from "react";
import {
  core,
  usAutocompletePro,
  internationalAddressAutocomplete,
} from "smartystreets-javascript-sdk";
import type { UseFormSetValue, Path, PathValue } from "react-hook-form";
import { ComboboxOption, ComboboxOptions } from "@headlessui/react";
import { cn } from "@/lib/utils";

import type { CheckoutFormData } from "../checkout.types";
import type { AddressFormData } from "@/networking/addresses.api";
import type { CreditCardFormData } from "@/networking/payments.api";
import {
  type EditPaymentMethodFormData,
  type InternalFormData,
} from "@/components/management/edit-payment-method-dialog.types";
import {
  resolveSmartyAdministrativeArea,
  type StateOption,
} from "@/lib/smarty-administrative-area";
const SmartyCore = core;
const USLookup = usAutocompletePro.Lookup;
const InternationalLookup = internationalAddressAutocomplete.Lookup;

const smartyKey = process.env.NEXT_PUBLIC_SMARTY_EMBEDDED_KEY || "";
const credentials = new SmartyCore.SharedCredentials(smartyKey);
const usClientBuilder = new SmartyCore.ClientBuilder(credentials).withLicenses([
  "us-autocomplete-pro-cloud",
]);
const usAutocompleteClient = usClientBuilder.buildUsAutocompleteProClient();
const internationalClientBuilder = new SmartyCore.ClientBuilder(
  credentials,
).withLicenses(["international-autocomplete-v2-cloud"]);
const internationalAutocompleteClient =
  internationalClientBuilder.buildInternationalAddressAutocompleteClient();

export type AddressSuggestion =
  | usAutocompletePro.Suggestion
  | InternationalAddressSuggestion;

interface InternationalAddressSuggestion {
  addressId?: string;
  addressText?: string;
  entries?: number;
  street?: string;
  locality?: string;
  administrativeArea?: string;
  administrativeAreaShort?: string;
  administrativeAreaLong?: string;
  postalCode?: string;
}

export default function useAutocomplete<
  T extends
    | CheckoutFormData
    | AddressFormData
    | CreditCardFormData
    | EditPaymentMethodFormData
    | InternalFormData,
>(
  setValue: UseFormSetValue<T>,
  cityField: Path<T>,
  stateField: Path<T>,
  postalCodeField: Path<T>,
  address1Field: Path<T>,
  stateOptions: readonly StateOption[] = [],
) {
  const [usAutocompleteOptions, setUsAutocompleteOptions] = useState<
    usAutocompletePro.Suggestion[]
  >([]);
  const [
    internationalAutocompleteOptions,
    setInternationalAutocompleteOptions,
  ] = useState<InternationalAddressSuggestion[]>([]);

  const getAutocompleteOptions = useCallback(
    async (input: string, formCountry: string) => {
      if (formCountry !== "United States") {
        setUsAutocompleteOptions([]);
        try {
          // @ts-expect-error -- SmartyStreets SDK type mismatch
          const summaryLookup = new InternationalLookup({
            search: input,
            country: formCountry,
          });
          const { result: summaryResults } =
            await internationalAutocompleteClient.send(summaryLookup);
          setInternationalAutocompleteOptions(summaryResults);
        } catch (err) {
          console.error(
            "Failed to fetch international autocomplete results:",
            err,
          );
        }
        return;
      }
      try {
        setInternationalAutocompleteOptions([]);
        const lookup = new USLookup(input);
        const { result } = await usAutocompleteClient.send(lookup);
        setUsAutocompleteOptions(result);
      } catch (err) {
        console.error("Failed to fetch autocomplete results:", err);
      }
    },
    [],
  );

  const processDetailedResults = (
    detailedResults: InternationalAddressSuggestion[],
  ) => {
    if (detailedResults?.length > 0) {
      const detailedResult = detailedResults?.[0];
      setValue(
        cityField,
        (detailedResult?.locality ?? "") as PathValue<T, Path<T>>,
        { shouldValidate: true },
      );
      setValue(
        stateField,
        resolveSmartyAdministrativeArea(
          detailedResult,
          stateOptions,
        ) as PathValue<T, Path<T>>,
        {
          shouldValidate: true,
        },
      );
      setValue(
        postalCodeField,
        (detailedResult?.postalCode ?? "") as PathValue<T, Path<T>>,
        {
          shouldValidate: true,
        },
      );
      setValue(
        address1Field,
        (detailedResult?.street ?? "") as PathValue<T, Path<T>>,
        {
          shouldValidate: true,
        },
      );
      setInternationalAutocompleteOptions([]);
    }
  };

  const fetchDetailedResults = async (
    addressId: string,
    country: string,
  ): Promise<InternationalAddressSuggestion[]> => {
    // @ts-expect-error -- SmartyStreets SDK type mismatch
    const detailedLookup = new InternationalLookup({ addressId }, country);
    detailedLookup.country = country;
    const { result: detailedResults } =
      await internationalAutocompleteClient.send(detailedLookup);
    return detailedResults;
  };

  const handleOptionClick = async (
    option: AddressSuggestion,
    formCountry: string,
  ) => {
    if (formCountry === "United States" && "streetLine" in option) {
      setValue(cityField, option?.city as PathValue<T, Path<T>>, {
        shouldValidate: true,
      });
      setValue(stateField, option?.state as PathValue<T, Path<T>>, {
        shouldValidate: true,
      });
      setValue(postalCodeField, option?.zipcode as PathValue<T, Path<T>>, {
        shouldValidate: true,
      });
      setValue(address1Field, option?.streetLine as PathValue<T, Path<T>>, {
        shouldValidate: true,
      });
      setUsAutocompleteOptions([]);
    } else {
      try {
        if (
          option?.entries === 1 &&
          "addressId" in option &&
          option.addressId
        ) {
          const detailedResults = await fetchDetailedResults(
            option.addressId,
            formCountry,
          );
          processDetailedResults(detailedResults);
        } else {
          const summaryLookup = new InternationalLookup(
            // @ts-expect-error -- SmartyStreets SDK type mismatch
            { addressId: option?.addressId },
            formCountry,
          );
          summaryLookup.country = formCountry;
          const { result: summaryResults } =
            await internationalAutocompleteClient.send(summaryLookup);
          setInternationalAutocompleteOptions(summaryResults);
        }
      } catch (error) {
        console.error(
          "Failed to fetch international autocomplete results:",
          error,
        );
      }
    }
  };

  const renderAutocompleteOptions = (isFocused: boolean) => {
    if (
      !isFocused ||
      (usAutocompleteOptions.length === 0 &&
        internationalAutocompleteOptions.length === 0)
    )
      return null;

    if (usAutocompleteOptions.length > 0) {
      return (
        <ComboboxOptions
          static
          className={cn(
            "transition-max-height absolute top-full left-0 z-50 w-full transform-gpu scroll-py-3 overflow-y-auto rounded-b shadow-lg duration-300 ease-in-out",
            "max-h-40 bg-gray-50 py-3 ring-1 ring-gray-300",
          )}
        >
          {usAutocompleteOptions.map((item) => (
            <ComboboxOption
              key={`${item.streetLine}-${item.city}-${item.state}-${item.entries}`}
              value={item}
              className="group flex cursor-pointer p-2 select-none data-[focus]:bg-gray-100 data-[focus]:outline-hidden"
            >
              <div className="flex-auto">
                <p className="text-sm font-medium text-gray-700 group-data-[focus]:text-gray-900">
                  {`${item.streetLine}, ${item.city}, ${item.state}`}
                </p>
              </div>
            </ComboboxOption>
          ))}
        </ComboboxOptions>
      );
    }

    if (internationalAutocompleteOptions.length > 0) {
      return (
        <ComboboxOptions
          static
          className={cn(
            "transition-max-height absolute top-full left-0 z-50 w-full transform-gpu scroll-py-3 overflow-y-auto rounded-b shadow-lg duration-300 ease-in-out",
            "max-h-40 bg-gray-50 py-3 ring-1 ring-gray-300",
          )}
        >
          {internationalAutocompleteOptions?.map((item) => (
            <ComboboxOption
              key={item?.addressId}
              value={item}
              className="group flex cursor-pointer p-2 select-none data-[focus]:bg-gray-100 data-[focus]:outline-hidden"
            >
              <div className="flex-auto">
                <p className="text-sm font-medium text-gray-700 group-data-[focus]:text-gray-900">
                  {item?.addressText}
                </p>
              </div>
            </ComboboxOption>
          ))}
        </ComboboxOptions>
      );
    }
  };

  return {
    usAutocompleteOptions,
    getAutocompleteOptions,
    handleOptionClick,
    renderAutocompleteOptions,
  };
}
